import type { Cell, FruitKind } from '../sim/types';
import { FRUITS } from './fruits';
import { LEVELS, type LevelDefinition } from './levels';

/**
 * The connected snowfield.
 *
 * Every authored room is 15x11 and they are stitched left to right into one
 * continuous level, each pair sharing the border column between them. That one
 * decision is what makes the whole idea cheap: pathfinding, Frostwalls, fruit,
 * enemies, and collision all keep working exactly as they do in a single room,
 * because as far as the simulation is concerned there is still only one grid.
 *
 * What the rooms add on top is:
 *  - a **door** in each shared column, sealed until that room's fruit is
 *    harvested, so clearing a room opens the way onward;
 *  - a **home room** per enemy, which is the furthest right it may ever go. A
 *    hunter can chase a cat back through every earlier room but can never
 *    follow one forward into a room it does not belong to.
 */
export const ROOM_WIDTH = 15;
export const ROOM_HEIGHT = 11;
/** Rooms overlap by their shared border column. */
export const ROOM_STRIDE = ROOM_WIDTH - 1;
/** The door sits in the shared column, on the row the authored doors use. */
export const DOOR_ROW = 1;

export interface WorldRoom {
  readonly index: number;
  readonly definition: LevelDefinition;
  /** Inclusive global column range, including both border columns. */
  readonly left: number;
  readonly right: number;
  /** Where a cat arrives when the run starts from this room. */
  readonly spawn: Cell;
}

export interface WorldDoor {
  readonly cell: Cell;
  /** Room whose harvest opens this door; it joins `room` and `room + 1`. */
  readonly room: number;
}

export interface WorldDefinition {
  readonly level: LevelDefinition;
  readonly rooms: readonly WorldRoom[];
  readonly doors: readonly WorldDoor[];
}

/** Global column where a room's left border sits. */
export function roomLeft(index: number): number {
  return index * ROOM_STRIDE;
}

/** Which room a global cell belongs to; a shared doorway starts the room ahead. */
export function roomIndexAt(cell: Cell, roomCount: number): number {
  const index = Math.floor(cell.x / ROOM_STRIDE);
  return Math.max(0, Math.min(roomCount - 1, index));
}

/**
 * Stitches the authored rooms into one map. Room 0 contributes all of its
 * columns; every later room contributes all but its left border, which is
 * already on the board as the previous room's right border.
 */
function stitchRows(levels: readonly LevelDefinition[]): string[] {
  const rows: string[] = [];
  for (let y = 0; y < ROOM_HEIGHT; y += 1) {
    let row = '';
    levels.forEach((definition, index) => {
      const source = definition.map[y] ?? '';
      row += index === 0 ? source : source.slice(1);
    });
    rows.push(row);
  }
  return rows;
}

/**
 * Per-room doors and spawns are authored as `E`/`K` inside each room, but a
 * continuous world needs one exit and real doorways in the shared columns, so
 * those glyphs are cleared and replaced by carved doors.
 */
function carveDoors(rows: string[], roomCount: number): WorldDoor[] {
  const grid = rows.map((row) => [...row]);
  const doors: WorldDoor[] = [];
  for (let y = 0; y < grid.length; y += 1) {
    const row = grid[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x += 1) {
      // Only the final room keeps a Snowgate; the rest become open floor with
      // a door carved into the shared column beside them.
      if (row[x] === 'E' && x < roomLeft(roomCount - 1)) row[x] = '.';
      if (row[x] === 'K') row[x] = '.';
    }
  }
  for (let room = 0; room < roomCount - 1; room += 1) {
    const x = roomLeft(room + 1);
    const row = grid[DOOR_ROW];
    if (!row) continue;
    row[x] = 'A';
    doors.push({ cell: { x, y: DOOR_ROW }, room });
  }
  rows.splice(0, rows.length, ...grid.map((row) => row.join('')));
  return doors;
}

/**
 * Only the first player spawn survives; later rooms' spawn glyphs would each
 * claim the cat, so they are flattened to floor.
 */
function keepFirstSpawns(rows: string[]): void {
  let seenP = false;
  let seenTwo = false;
  for (let y = 0; y < rows.length; y += 1) {
    const row = [...(rows[y] ?? '')];
    for (let x = 0; x < row.length; x += 1) {
      if (row[x] === 'P') {
        if (seenP) row[x] = '.';
        seenP = true;
      } else if (row[x] === '2') {
        if (seenTwo) row[x] = '.';
        seenTwo = true;
      }
    }
    rows[y] = row.join('');
  }
}

/** The room's own `P` glyph, in world coordinates. */
function findSpawn(definition: LevelDefinition, index: number): Cell {
  for (let y = 0; y < definition.map.length; y += 1) {
    const x = (definition.map[y] ?? '').indexOf('P');
    if (x >= 0) return { x: roomLeft(index) + x, y };
  }
  return { x: roomLeft(index) + 1, y: 1 };
}

export function buildWorld(levels: readonly LevelDefinition[] = LEVELS): WorldDefinition {
  const first = levels[0];
  if (!first) throw new Error('The connected world needs at least one room.');
  const rows = stitchRows(levels);
  const doors = carveDoors(rows, levels.length);
  keepFirstSpawns(rows);

  const rooms: WorldRoom[] = levels.map((definition, index) => ({
    index,
    definition,
    left: roomLeft(index),
    right: roomLeft(index) + ROOM_WIDTH - 1,
    // Each room keeps its authored cat spawn, offset into world coordinates,
    // so entering the snowfield at a room drops you where it was designed for.
    spawn: findSpawn(definition, index),
  }));

  // One fruit family per room, harvested in order, so clearing a room's batch
  // is exactly what opens its door. Rooms prefer the family they were authored
  // around, then any family they use, then whatever is still unclaimed, so the
  // snowfield never repeats a family and each room keeps its own character.
  const claimed = new Set<FruitKind>();
  const everyFamily = FRUITS.map((fruit) => fruit.id);
  const fruitOrder: FruitKind[] = levels.map((definition) => {
    const preferred = definition.fruitOrder.find((kind) => !claimed.has(kind))
      ?? everyFamily.find((kind) => !claimed.has(kind))
      ?? definition.fruitOrder[0]
      ?? 'strawberry';
    claimed.add(preferred);
    return preferred;
  });

  const level: LevelDefinition = {
    ...first,
    id: first.id,
    name: 'SNOWFIELD',
    kicker: 'One connected world',
    objective: 'Harvest each room to open the way onward. You can always double back.',
    targetTimeMs: levels.reduce((total, definition) => total + definition.targetTimeMs, 0),
    automaticShiftMs: null,
    ranks: {
      B: levels.reduce((total, definition) => total + definition.ranks.B, 0),
      A: levels.reduce((total, definition) => total + definition.ranks.A, 0),
      S: levels.reduce((total, definition) => total + definition.ranks.S, 0),
    },
    fruitOrder,
    map: rows,
    // The Warden encounter is authored around a single arena, so it stays out
    // of the connected run until it is redesigned for it.
    boss: false,
  };

  return { level, rooms, doors };
}
